import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const PublicLeadForm = () => {
  const { formId } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/lead-forms/${formId}/public`)
      .then(r => { if (!r.ok) throw new Error("Form not found"); return r.json(); })
      .then(data => {
        setForm(data);
        const init = {};
        for (const f of data.fields) init[f.key] = "";
        setValues(init);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [formId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (values._hp) return; // honeypot
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/lead-forms/${formId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: values, honeypot: values._hp || "" }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Submission failed");
      }
      setSubmitted(true);
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  if (loading) return (
    <div style={styles.page}>
      <div style={styles.card}><Loader2 className="w-8 h-8 animate-spin text-blue-500 mx-auto" /></div>
    </div>
  );

  if (error && !form) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
        <p style={{ textAlign: "center", color: "#ef4444", fontSize: 14 }}>{error}</p>
      </div>
    </div>
  );

  if (submitted) return (
    <div style={styles.page}>
      <div style={styles.card}>
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
        <h2 style={{ fontSize: 22, fontWeight: 700, textAlign: "center", marginBottom: 8 }}>Thank You!</h2>
        <p style={{ textAlign: "center", color: "#6b7280", fontSize: 14, lineHeight: 1.6 }}>
          Your request has been submitted. We'll be in touch shortly.
        </p>
        {form?.business_phone && (
          <p style={{ textAlign: "center", marginTop: 16, fontSize: 13, color: "#9ca3af" }}>
            Need immediate help? Call <a href={`tel:${form.business_phone}`} style={{ color: "#3b82f6", fontWeight: 600 }}>{form.business_phone}</a>
          </p>
        )}
      </div>
    </div>
  );

  const fields = [...(form?.fields || [])].sort((a, b) => a.order - b.order);

  return (
    <div style={styles.page} data-testid="public-form-page">
      <form onSubmit={handleSubmit} style={styles.card} data-testid="lead-form">
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          {form.logo_url && (
            <img src={form.logo_url} alt="" style={{ height: 40, margin: "0 auto 12px", objectFit: "contain" }} />
          )}
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>
            {form.name || "Contact Us"}
          </h1>
          {form.business_name && (
            <p style={{ fontSize: 13, color: "#9ca3af", marginTop: 4 }}>{form.business_name}</p>
          )}
        </div>

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#dc2626" }}>
            {error}
          </div>
        )}

        {/* Fields */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {fields.map(field => (
            <div key={field.key}>
              <label style={styles.label}>
                {field.label}{field.required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
              </label>
              {field.type === "textarea" ? (
                <textarea
                  value={values[field.key] || ""}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  required={field.required}
                  rows={4}
                  style={{ ...styles.input, minHeight: 100, resize: "vertical" }}
                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                  data-testid={`field-${field.key}`}
                />
              ) : field.type === "select" && field.options?.length > 0 ? (
                <select
                  value={values[field.key] || ""}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  required={field.required}
                  style={styles.input}
                  data-testid={`field-${field.key}`}
                >
                  <option value="">Select {field.label}...</option>
                  {field.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  type={field.type === "tel" ? "tel" : field.type === "email" ? "email" : field.type === "date" ? "date" : "text"}
                  value={values[field.key] || ""}
                  onChange={e => setValues(v => ({ ...v, [field.key]: e.target.value }))}
                  required={field.required}
                  style={styles.input}
                  placeholder={field.type === "tel" ? "(555) 123-4567" : field.type === "email" ? "you@email.com" : `Enter ${field.label.toLowerCase()}...`}
                  data-testid={`field-${field.key}`}
                />
              )}
            </div>
          ))}
        </div>

        {/* Honeypot */}
        <input type="text" name="_hp" value={values._hp || ""} onChange={e => setValues(v => ({ ...v, _hp: e.target.value }))} style={{ position: "absolute", left: -9999, opacity: 0, height: 0 }} tabIndex={-1} autoComplete="off" />

        {/* Submit */}
        <button
          type="submit"
          disabled={submitting}
          style={{
            ...styles.btn,
            opacity: submitting ? 0.7 : 1,
            cursor: submitting ? "not-allowed" : "pointer",
          }}
          data-testid="form-submit-btn"
        >
          {submitting ? "Submitting..." : "Submit Request"}
        </button>

        <p style={{ textAlign: "center", fontSize: 11, color: "#d1d5db", marginTop: 20 }}>
          Powered by Fieldz AI
        </p>
      </form>
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    background: "linear-gradient(135deg, #f0f4ff 0%, #faf5ff 50%, #fef3f2 100%)",
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  card: {
    width: "100%",
    maxWidth: 480,
    background: "white",
    borderRadius: 16,
    padding: "36px 32px",
    boxShadow: "0 10px 40px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)",
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: "#374151",
    marginBottom: 6,
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    fontSize: 14,
    border: "1.5px solid #e5e7eb",
    borderRadius: 10,
    outline: "none",
    transition: "border-color 0.2s, box-shadow 0.2s",
    background: "#fafafa",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  btn: {
    width: "100%",
    marginTop: 24,
    padding: "14px 20px",
    fontSize: 15,
    fontWeight: 700,
    color: "white",
    background: "linear-gradient(135deg, #3b82f6, #6366f1)",
    border: "none",
    borderRadius: 12,
    boxShadow: "0 4px 14px rgba(59,130,246,0.35)",
    fontFamily: "inherit",
    letterSpacing: "0.01em",
  },
};

export default PublicLeadForm;
